import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, RefreshCw, ChevronRight } from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  draft:      'bg-gray-100 text-gray-600',
  approved:   'bg-blue-50 text-blue-700',
  processing: 'bg-amber-50 text-amber-700',
  completed:  'bg-emerald-50 text-emerald-700',
  failed:     'bg-red-50 text-red-600',
};

const NEXT_ACTIONS = {
  draft:      [{ label: 'Approve',    status: 'approved' }, { label: 'Process', status: 'processing' }],
  approved:   [{ label: 'Mark Processing', status: 'processing' }],
  processing: [{ label: 'Mark Completed', status: 'completed' }, { label: 'Mark Failed', status: 'failed' }],
};

const AdminMarketplacePayouts = () => {
  const [batches, setBatches] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [actionState, setActionState] = useState({});
  const [notesModal, setNotesModal] = useState(null);
  const [notesText, setNotesText] = useState('');
  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API_URL}/api/admin/marketplace/payouts?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setBatches(data.items || []);
      setTotal(data.total || 0);
    } catch {
      // leave stale data
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    const token = localStorage.getItem('admin_token');
    try {
      const res = await fetch(`${API_URL}/api/admin/marketplace/payouts/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        await load();
        alert(`Generated ${data.created || 0} batch(es).`);
      } else {
        alert(data.detail || 'Failed to generate batches.');
      }
    } catch {
      alert('Request failed.');
    } finally {
      setGenerating(false);
    }
  };

  const applyTransition = async (batchId, newStatus, notes) => {
    const token = localStorage.getItem('admin_token');
    setActionState(s => ({ ...s, [batchId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/admin/marketplace/payouts/${batchId}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, ...(notes ? { admin_notes: notes } : {}) }),
      });
      if (res.ok) {
        await load();
      } else {
        const data = await res.json().catch(() => ({}));
        const msg = typeof data.detail === 'string' ? data.detail : data.detail?.message || `Error ${res.status}`;
        alert(msg);
      }
    } catch {
      alert('Request failed.');
    } finally {
      setActionState(s => ({ ...s, [batchId]: false }));
    }
  };

  const openNotesModal = (batchId, newStatus) => {
    setNotesModal({ batchId, newStatus });
    setNotesText('');
  };

  const confirmNotesModal = () => {
    if (!notesModal) return;
    applyTransition(notesModal.batchId, notesModal.newStatus, notesText.trim() || null);
    setNotesModal(null);
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Marketplace Payouts</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage creator payout batches</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors"
            >
              {generating ? 'Generating…' : 'Generate Draft Batches'}
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          {['', 'draft', 'approved', 'processing', 'completed', 'failed'].map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                statusFilter === s
                  ? 'bg-violet-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-16">
              <Wallet className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No payout batches found</p>
              <p className="text-xs text-gray-400 mt-1">Generate draft batches when creators have available balances above the payout threshold.</p>
            </div>
          ) : (
            <>
              <div className="hidden md:grid grid-cols-6 gap-4 px-5 py-3 bg-gray-50 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                <div className="col-span-2">Creator / Period</div>
                <div className="text-right">Amount</div>
                <div className="text-center">Status</div>
                <div>Date</div>
                <div>Actions</div>
              </div>
              <div className="divide-y divide-gray-50">
                {batches.map((b) => {
                  const actions = NEXT_ACTIONS[b.status] || [];
                  const busy = actionState[b.id];
                  return (
                    <div key={b.id} className="grid grid-cols-1 md:grid-cols-6 gap-2 md:gap-4 px-5 py-4 items-center">
                      <div className="col-span-2 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{b.creator_id}</p>
                        <p className="text-xs text-gray-400">{b.batch_period}</p>
                        {b.admin_notes && (
                          <p className="text-xs text-gray-400 italic mt-0.5 truncate">{b.admin_notes}</p>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-900 md:text-right">
                        ${((b.total_amount_cents || 0) / 100).toFixed(2)}
                      </p>
                      <div className="flex md:justify-center">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[b.status] || 'bg-gray-100 text-gray-500'}`}>
                          {b.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400">
                        {b.created_at ? new Date(b.created_at).toLocaleDateString() : '—'}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {actions.map(action => (
                          <button
                            key={action.status}
                            disabled={busy}
                            onClick={() => openNotesModal(b.id, action.status)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-violet-50 hover:text-violet-700 disabled:opacity-50 transition-colors"
                          >
                            {action.label}
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {total > LIMIT && (
                <div className="px-5 py-4 border-t border-gray-50 flex justify-end gap-2">
                  {page > 1 && (
                    <button onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50">
                      Previous
                    </button>
                  )}
                  {batches.length === LIMIT && (
                    <button onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700">
                      Next
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Notes modal */}
      {notesModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="font-semibold text-gray-900 mb-1">
              Transition to <span className="capitalize">{notesModal.newStatus}</span>
            </h3>
            <p className="text-sm text-gray-500 mb-4">Add optional admin notes for this status change.</p>
            <textarea
              value={notesText}
              onChange={e => setNotesText(e.target.value)}
              placeholder="Optional notes…"
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setNotesModal(null)}
                className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmNotesModal}
                className="px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminMarketplacePayouts;
